/**
 * 1stbuddha — product.js
 *
 * <product-gallery>  (snippets/product-gallery.liquid)
 *   - thumbnails scroll the snap track to their image; the track's scroll
 *     position drives aria-current on thumbs, the "1 / 6" counter and the
 *     mobile progress segments
 *   - arrow keys on the focused track move one image
 *   - [data-zoom-open] opens the zoom modal scrolled to the current image
 *     (or the index in the attribute)
 *
 * <product-info>  (sections/product.liquid)
 *   - sticky buy bar (mobile/tablet): visible while the in-page actions are
 *     out of view and the page is scrolled past them; toggles
 *     body.has-sticky-bar so the advisor button and toasts lift above it
 *   - [data-share]: native share sheet, else copy the link + toast
 *   - opens a dialog marked [data-open-on-load] (offer form after posting)
 *   - records the piece for "Recently viewed" (localStorage `recently-viewed`,
 *     last 12 handles, this browser only; read by assets/recently-viewed.js)
 *
 * <product-recommendations>  (sections/product-recommendations.liquid)
 *   - with data-url, fetches Shopify's product recommendations for the
 *     section once it nears the viewport and swaps its content in
 */
(() => {
  const config = (() => {
    try {
      return JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
    } catch {
      return {};
    }
  })();
  const strings = config.strings || {};
  const toast = (detail) => document.dispatchEvent(new CustomEvent('toast:show', { detail }));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('product-gallery')) {
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.thumbs = [...this.querySelectorAll('[data-thumb]')];
        this.segments = [...this.querySelectorAll('.gallery__segment')];
        this.counter = this.querySelector('[data-counter]');
        this.index = 0;
        if (!this.track) return;

        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-thumb]');
          if (thumb) {
            this.go(Number(thumb.dataset.thumb));
            return;
          }
          const zoom = event.target.closest('[data-zoom-open]');
          if (zoom) this.openZoom(zoom.dataset.zoomOpen ? Number(zoom.dataset.zoomOpen) : this.index, zoom);
        });

        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(Math.min(this.index + 1, this.slides.length - 1));
          if (event.key === 'ArrowLeft') this.go(Math.max(this.index - 1, 0));
        });

        let frame = null;
        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              const width = this.track.clientWidth || 1;
              this.setIndex(Math.round(this.track.scrollLeft / width));
            });
          },
          { passive: true }
        );
      }

      go(index) {
        const slide = this.slides[index];
        if (!slide) return;
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        this.setIndex(index);
      }

      setIndex(index) {
        if (index === this.index || index < 0 || index >= this.slides.length) return;
        this.index = index;
        this.thumbs.forEach((thumb, i) => {
          if (i === index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        this.segments.forEach((segment, i) => segment.classList.toggle('is-active', i === index));
        if (this.counter) this.counter.textContent = String(index + 1);
        this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      openZoom(index, trigger) {
        const dialog = document.getElementById(this.dataset.zoom);
        const component = dialog?.closest('dialog-component');
        if (!component) return;
        component.open(trigger);
        const item = dialog.querySelector(`[data-zoom-item="${index}"]`);
        requestAnimationFrame(() => item?.scrollIntoView({ block: 'start' }));
      }
    }
    customElements.define('product-gallery', ProductGallery);
  }

  if (!customElements.get('product-info')) {
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('[data-sticky-bar]');
        this.actions = this.querySelector('[data-product-actions]');
        if (this.bar && this.actions && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(([entry]) => {
            // Show once the buttons have scrolled up out of view (not before reaching them).
            const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggleBar(past);
          });
          this.observer.observe(this.actions);
        }

        this.addEventListener('click', (event) => {
          const share = event.target.closest('[data-share]');
          if (share) this.share(share);
        });

        const pending = this.querySelector('[data-open-on-load]');
        if (pending) pending.closest('dialog-component')?.open();

        this.recordView();
      }

      recordView() {
        const handle = this.dataset.productHandle;
        if (!handle || window.Shopify?.designMode) return;
        try {
          const stored = JSON.parse(window.localStorage.getItem('recently-viewed') || '[]');
          const list = Array.isArray(stored) ? stored.filter((item) => typeof item === 'string' && item !== handle) : [];
          window.localStorage.setItem('recently-viewed', JSON.stringify([handle, ...list].slice(0, 12)));
        } catch {
          /* storage unavailable */
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.body.classList.remove('has-sticky-bar');
      }

      toggleBar(visible) {
        this.bar.classList.toggle('is-visible', visible);
        this.bar.toggleAttribute('inert', !visible);
        this.bar.setAttribute('aria-hidden', String(!visible));
        document.body.classList.toggle('has-sticky-bar', visible && getComputedStyle(this.bar).display !== 'none');
      }

      async share(button) {
        const url = button.dataset.shareUrl || window.location.href;
        const title = button.dataset.shareTitle || document.title;
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch {
            /* dismissed */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          toast({ message: strings.linkCopied || url });
        } catch {
          toast({ message: url });
        }
      }
    }
    customElements.define('product-info', ProductInfo);
  }

  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url) return;
        const load = () => this.load();
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              if (!entry.isIntersecting) return;
              this.observer.disconnect();
              load();
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          this.observer.observe(this);
        } else {
          load();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const html = await response.text();
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            window.themeWishlist?.sync(this);
          }
        } catch {
          /* recommendations are optional */
        }
      }
    }
    customElements.define('product-recommendations', ProductRecommendations);
  }
})();
